<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useClipboardStore } from '@/stores/clipboard'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Menu } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import AppShell from '@lib/components/shared/AppShell.vue'
import AppSidebar from '@lib/components/shared/AppSidebar.vue'
import AppTopbar from '@lib/components/shared/AppTopbar.vue'
import BreadcrumbBar from '@lib/components/shared/BreadcrumbBar.vue'
import InstanceSwitcher from '@lib/components/shared/InstanceSwitcher.vue'
import UserMenu from '@lib/components/shared/UserMenu.vue'
import { navGroups, navItems } from '@/config/nav'
import logoUrl from '@/assets/logo-home-bzuco.png'

const ui = useUiStore()
const auth = useAuthStore()
const clipboard = useClipboardStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const filteredGroups = computed(() =>
    navGroups.map(group => ({
        key: group.key,
        label: t(group.labelKey),
        items: group.items.map(item => ({
            ...item,
            label: t(item.labelKey),
            visible: !item.requiredRight || auth.hasRight(item.requiredRight),
            badge: item.key === 'clipboard' && clipboard.count > 0 ? clipboard.count : undefined
        }))
    }))
)

const activeKey = computed(() => {
    const found = navItems.find(i => route.path === i.to || route.path.startsWith(i.to + '/'))
    return found?.key
})

// ESC closes the mobile drawer. Listener is on window so it works regardless
// of focus (e.g. user opened drawer, then tapped backdrop area which doesn't
// receive keyboard focus).
function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && ui.mobileDrawerOpen) {
        ui.closeMobileDrawer()
    }
}
onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))
</script>

<template>
    <AppShell :sidebar-collapsed="ui.sidebarCollapsed">
        <template #sidebar>
            <AppSidebar
                :groups="filteredGroups"
                :active-key="activeKey"
                :collapsed="ui.sidebarCollapsed"
                :mobile-open="ui.mobileDrawerOpen"
                :logo-src="logoUrl"
                app-name="Inspector"
                @navigate="(to: string) => router.push(to)"
                @close-mobile="ui.closeMobileDrawer"
            />
        </template>
        <template #topbar>
            <AppTopbar :sidebar-collapsed="ui.sidebarCollapsed">
                <template #sidebar-toggle>
                    <!-- Mobile hamburger (< lg) — toggles off-canvas drawer. -->
                    <button
                        type="button"
                        class="lg:hidden flex items-center justify-center w-[30px] h-[30px] rounded-6 text-gray-400 hover:bg-gray-100 hover:text-ink transition-colors duration-fast shrink-0"
                        :title="ui.mobileDrawerOpen ? t('common.nav.closeMenu') : t('common.nav.openMenu')"
                        @click="ui.toggleMobileDrawer"
                    >
                        <Menu class="w-5 h-5" />
                    </button>
                    <!-- Desktop collapse toggle (lg+) — collapses to icons-only. -->
                    <button
                        type="button"
                        class="hidden lg:flex items-center justify-center w-[30px] h-[30px] rounded-6 text-gray-400 hover:bg-gray-100 hover:text-ink transition-colors duration-fast shrink-0"
                        :title="ui.sidebarCollapsed ? t('common.nav.expandSidebar') : t('common.nav.collapseSidebar')"
                        @click="ui.toggleSidebar"
                    >
                        <svg
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.8"
                            viewBox="0 0 24 24"
                            class="w-4 h-4"
                        >
                            <rect x="3" y="3" width="18" height="18" rx="3" />
                            <path d="M9 3v18" />
                            <path v-if="!ui.sidebarCollapsed" d="M6 9l-2 3 2 3" />
                            <path v-else d="M6 9l2 3-2 3" />
                        </svg>
                    </button>
                </template>
                <template #breadcrumb>
                    <!-- Hide breadcrumbs under sm to save horizontal space. -->
                    <div class="hidden sm:block min-w-0 flex-1">
                        <BreadcrumbBar
                            :items="ui.breadcrumbs"
                            root-label="Inspector"
                            root-to="/"
                            @navigate="(to: string) => router.push(to)"
                        />
                    </div>
                </template>
                <template #instance-switcher>
                    <InstanceSwitcher
                        :options="auth.instances"
                        :active-id="auth.activeInstance?.id ?? null"
                        @change="inst => auth.changeInstance(inst.name)"
                    />
                </template>
                <template #user-menu>
                    <UserMenu
                        :name="
                            auth.user ? `${auth.user.firstName ?? ''} ${auth.user.lastName ?? ''}`.trim() : undefined
                        "
                        :email="auth.user?.email"
                        :locales="[
                            { code: 'cs', label: 'CS' },
                            { code: 'en', label: 'EN' }
                        ]"
                        :active-locale="ui.locale"
                        :theme="ui.theme"
                        :theme-label="t('common.theme.label')"
                        :theme-option-labels="{
                            light: t('common.theme.light'),
                            auto: t('common.theme.auto'),
                            dark: t('common.theme.dark')
                        }"
                        @logout="
                            async () => {
                                await auth.logout()
                                router.push({ name: 'login' })
                            }
                        "
                        @set-locale="(code: string) => ui.setLocale(code)"
                        @set-theme="(value: 'auto' | 'light' | 'dark') => ui.setTheme(value)"
                    />
                </template>
            </AppTopbar>
        </template>
        <slot />
    </AppShell>
    <!--
        Mobile backdrop. Visible only when off-canvas drawer is open and only
        below lg (sidebar is permanent at lg+). z-[90] sits below z-sidebar
        (100) so the sidebar renders on top of the backdrop. Tap closes drawer.
    -->
    <div
        v-if="ui.mobileDrawerOpen"
        class="fixed inset-0 z-[90] bg-black/50 lg:hidden"
        @click="ui.closeMobileDrawer"
    />
</template>
